import ClickAwayListener from "@mui/material/ClickAwayListener";
import { COUNTRY_CURRENCIES } from "@redotech/money/countries";
import { CountryCurrency } from "@redotech/money/country";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { getKey } from "@redotech/react-util/key";
import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import ChevronDown from "@redotech/redo-web/icon/chevron-down.svg";
import LeftArrow from "@redotech/redo-web/icon/left-arrow.svg";
import * as link from "@redotech/redo-web/link.module.css";
import * as classNames from "classnames";
import { ReactNode, memo, useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { SetAuthContext } from "../app/auth";
import { CurrencyContext } from "../app/currency";
import { OrderContext } from "../app/order";
import { SettingsContext } from "../app/settings";
import { SetUserContext, UserContext } from "../app/user";
import { AdvancedExchangeContext } from "../contexts/AdvancedExchangeContext";
import { CurrentItemBeingReturnedContext } from "../contexts/CurrentItemBeingReturnedContext";
import { ExchangeTypeContext } from "../contexts/ExchangeTypeContext";
import { FlowStepContext } from "../contexts/FlowStepContext";
import { ItemsReadyForReturnContext } from "../contexts/ItemsReadyForReturnContext";
import { StorefrontCartContext } from "../contexts/StorefrontCartContext";
import {
  getProductsFromReturnableItems,
  isVariantExchange,
} from "../utils/utils";
import * as navbarCss from "./navbar.module.css";
import * as page from "./page.module.css";

export const Navbar = memo(
  ({
    children,
    className = "",
  }: {
    children?: ReactNode;
    className?: string;
  }) => {
    const navigate = useNavigate();
    const user = useContext(UserContext);
    const setUser = useContext(SetUserContext);
    const setAuth_ = useContext(SetAuthContext);
    const settings = useContext(SettingsContext);
    const { flowStep, setFlowStep, submitting } = useContext(FlowStepContext);
    const { exchangeType, setExchangeType, setExchangeGroup } =
      useContext(ExchangeTypeContext);
    const currentOrder = useContext(OrderContext);
    const { currentItemBeingReturned, setCurrentItemBeingReturned } =
      useContext(CurrentItemBeingReturnedContext);
    const {
      itemsReadyForReturn,
      availableReturnOptions,
      setAvailableReturnOptions,
      setItemsReadyForReturn,
    } = useContext(ItemsReadyForReturnContext);
    const size = useScreenType();
    const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
    const {
      cartVisible,
      setCartMenuOpen,
      setCartVisible,
      setSelectedSuggestedProduct,
    } = useContext(AdvancedExchangeContext);
    const { shopifyStorefrontCart, setShopifyStorefrontCart } = useContext(
      StorefrontCartContext,
    );
    const { currency, formatCurrency } = useContext(CurrencyContext);

    const getInitials = (string) => {
      if (!string) {
        return;
      }
      const names = string.split(" ");
      let initials = names[0].substring(0, 1).toUpperCase();

      if (names.length > 1) {
        initials += names[names.length - 1].substring(0, 1).toUpperCase();
      }
      return initials;
    };

    const getTaxForProduct = (product: any) => {
      if (!product) {
        return 0;
      }
      const shopifyLineItem = currentOrder?.shopify.line_items.find(
        (lineItem) => lineItem.id === product.id,
      );
      if (!shopifyLineItem) {
        return 0;
      }
      const tax = shopifyLineItem.tax_lines
        ? shopifyLineItem.tax_lines.reduce(
            (totalTax: number, taxLine: any) =>
              totalTax + parseFloat(taxLine.price),
            0,
          ) / shopifyLineItem.quantity
        : 0;

      return tax;
    };

    // todo used in several spots, move to a utils
    const storeCreditAvailable = () => {
      let storeCredit = 0;
      if (exchangeType === "ADVANCED") {
        storeCredit +=
          (currentItemBeingReturned?.lineItemSource.valueAfterAdjustment ||
            0.0) + getTaxForProduct(currentItemBeingReturned?.lineItemSource);
      }
      if (itemsReadyForReturn.length > 0) {
        getProductsFromReturnableItems(itemsReadyForReturn).forEach((item) => {
          if (!isVariantExchange(item)) {
            storeCredit += item.valueAfterAdjustment + getTaxForProduct(item);
          }
        });
      }
      if (shopifyStorefrontCart) {
        storeCredit -=
          parseFloat(
            shopifyStorefrontCart?.cost?.totalTaxAmount?.amount || 0.0,
          ) +
          parseFloat(
            shopifyStorefrontCart?.cost?.checkoutChargeAmount?.amount || 0.0,
          );
      }

      return storeCredit;
    };

    if (!user) {
      return <>{children}</>;
    }
    return (
      <div>
        <div className={classNames(className, page.navBarContent)}>
          <div
            className={page.backArrow}
            onClick={() => {
              if (flowStep === 3 || submitting) {
                return;
              }
              setCartVisible(false);
              setCartMenuOpen(false);
              setExchangeType(undefined);
              setSelectedSuggestedProduct(undefined);
              setExchangeGroup(undefined);
              if (!flowStep || flowStep === 1) {
                if (
                  !!availableReturnOptions?.shopNow ||
                  !!availableReturnOptions?.refund ||
                  !!availableReturnOptions?.storeCredit
                ) {
                  setAvailableReturnOptions({});
                  setCurrentItemBeingReturned(undefined);
                } else if (currentItemBeingReturned) {
                  // used for mobile
                  setCurrentItemBeingReturned(undefined);
                }
                navigate(-1);
              } else if (flowStep && flowStep > 1) {
                if (flowStep === 2) {
                  // prevent the page from automatically going to the review page if the user manually goes back
                  const numAvailableReturnOptions = Object.values(
                    availableReturnOptions,
                  ).filter((option: any) => option).length;
                  if (
                    itemsReadyForReturn.length === 1 &&
                    numAvailableReturnOptions <= 1 &&
                    currentOrder.returnableItems.length === 1
                  ) {
                    setItemsReadyForReturn([]);
                  }
                  setAvailableReturnOptions({});
                }
                setFlowStep(flowStep - 1);
              } else {
                navigate(-1);
              }
            }}
          >
            {flowStep !== 3 && !submitting && <StoreReturn />}
          </div>
          <div className={page.imageWrapper}>
            <img
              id="navbar-logo"
              src={settings.theme?.logo_image}
              className={page.navBarLogo}
            />
          </div>
          <div className={page.sideButtons}>
            {size === ScreenType.DESKTOP && (
              <div
                className={page.cartContainer}
                style={
                  cartVisible
                    ? { visibility: "inherit" }
                    : { visibility: "hidden" }
                }
                onClick={() => {
                  if (cartVisible) {
                    setCartMenuOpen(true);
                  }
                }}
              >
                {storeCreditAvailable() > 0 && (
                  <div
                    className={classNames(page.cartTotalText, page.textSuccess)}
                  >
                    {formatCurrency(storeCreditAvailable())}
                  </div>
                )}
                <div className={page.cartSizeBubble}>
                  {shopifyStorefrontCart?.lines?.edges?.length || 0}
                </div>
                <svg
                  className={page.cart}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#000000"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="10" cy="20.5" r="1" />
                  <circle cx="18" cy="20.5" r="1" />
                  <path d="M2.5 2.5h3l2.7 12.4a2 2 0 0 0 2 1.6h7.7a2 2 0 0 0 2-1.6l1.6-8.4H7.1" />
                </svg>
              </div>
            )}
            <div className={page.account}>
              {size === ScreenType.DESKTOP ? (
                <div className={page.accountText}>
                  <div className={page.nameText}>
                    {/*Show guest name if there is one*/}
                    {user.guestName || user.name}
                  </div>
                  {currency && <CurrencySelect />}
                </div>
              ) : (
                currency && <CurrencySelect />
              )}

              <div
                className={page.avatarContainer}
                onClick={() =>
                  setAccountDropdownOpen(accountDropdownOpen ? false : true)
                }
              >
                <div className={page.avatar}>
                  {getInitials(user.guestName || user.name)}
                </div>
                <div className={page.chevronDown}>
                  <ChevronDown />
                </div>
              </div>

              {accountDropdownOpen && (
                <ClickAwayListener
                  onClickAway={() => setAccountDropdownOpen(false)}
                >
                  <div className={page.accountDropdown}>
                    {!user.guestEmail && (
                      <div className={page.accountMenuListItem}>
                        <Link to={"/returns-portal/orders"}>My orders</Link>
                      </div>
                    )}
                    <div
                      className={page.accountMenuListItem}
                      onClick={async () => {
                        setAuth_(undefined);
                        setUser(undefined);
                        await new Promise((resolve) => setTimeout(resolve, 0));
                        navigate("/returns-portal/login");
                      }}
                    >
                      Logout
                    </div>
                  </div>
                </ClickAwayListener>
              )}
            </div>
          </div>
        </div>
        {children}
      </div>
    );
  },
);

const CurrencyOption = memo(
  ({ currency, onClick }: { currency: CountryCurrency; onClick(): void }) => {
    return (
      <button
        className={navbarCss.currencyOption}
        onClick={onClick}
        type="button"
      >
        {currency.currencyCode} | {currency.country}
      </button>
    );
  },
);

const CurrencySelect = memo(() => {
  const screenSize = useScreenType();
  const { currency, setCurrency } = useContext(CurrencyContext);
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  return (
    <>
      <div
        className={classNames(
          navbarCss.currencyText,
          screenSize === ScreenType.MOBILE && navbarCss.mobile,
        )}
        onClick={() => {
          setCurrencyDropdownOpen((value) => !value);
        }}
      >
        {screenSize === ScreenType.DESKTOP ? (
          <>
            {currency.currencyCode} | {currency.country}
          </>
        ) : (
          currency.currencyCode
        )}
      </div>
      {currencyDropdownOpen && (
        <ClickAwayListener onClickAway={() => setCurrencyDropdownOpen(false)}>
          <div className={navbarCss.currencyDropdown}>
            <IterableMap
              items={COUNTRY_CURRENCIES.all()}
              keyFn={(currency) => getKey(currency)}
            >
              {(currency) => {
                const handleClick = useHandler(() => {
                  setCurrency(currency);
                  setCurrencyDropdownOpen(false);
                });
                return (
                  <CurrencyOption currency={currency} onClick={handleClick} />
                );
              }}
            </IterableMap>
          </div>
        </ClickAwayListener>
      )}
    </>
  );
});

const StoreReturn = memo(({ className = "" }: { className?: string }) => {
  const screenSize = useScreenType();

  return (
    <a className={classNames(className, page.storeReturn, link.link)}>
      <LeftArrow />
      {screenSize === ScreenType.DESKTOP && "Back"}
    </a>
  );
});
